import { BaseApiClient } from './base-client';
import { isInMiniApp } from '../../utils/authHeaders';
import { downloadFileToBrowser, generateDownloadFileName } from '../../utils/downloadUtils';

/**
 * File Delivery API
 * Единый сервис для доставки файлов пользователям
 * 
 * Автоматически определяет контекст:
 * - Telegram Mini App → отправка в чат
 * - Web Browser → скачивание файла
 */

export type FileType = 'image' | 'video' | 'document' | 'csv' | 'json' | 'pdf' | 'python' | 'audio' | 'auto';

export interface DeliveryOptions {
  /** URL файла */
  url: string;
  /** Тип файла (auto для автоопределения) */
  type?: FileType;
  /** Имя файла */
  fileName?: string;
  /** MIME тип */
  mimeType?: string;
  /** ID файла (для генерации имени) */
  id?: string;
  /** Промпт (для caption в Telegram) */
  prompt?: string;
  /** Модель (для caption в Telegram) */
  model?: string;
  /** S3 ключ (для обновления URL) */
  s3Key?: string;
}

export interface BatchDeliveryItem {
  url: string;
  type?: 'image' | 'video';
  isFirst?: boolean;
}

export interface DeliveryResult {
  success: boolean;
  message?: string;
  error?: string;
  code?: string;
}

export class FileDeliveryApi extends BaseApiClient {
  
  // ===== УНИВЕРСАЛЬНЫЕ МЕТОДЫ =====
  
  /**
   * Доставить файл пользователю
   * Автоматически выбирает способ доставки:
   * - Mini App → отправка в Telegram чат
   * - Browser → скачивание файла
   */
  async deliverFile(options: DeliveryOptions): Promise<DeliveryResult> {
    const { url, type = 'auto', fileName, mimeType, id, prompt, model } = options;
    
    if (!url) {
      return { success: false, error: 'URL файла обязателен' };
    }
    
    // Определяем имя файла
    const resolvedFileName = fileName || this.generateFileName(type, model, id);
    
    try {
      if (isInMiniApp()) {
        // В Mini App - отправляем в Telegram чат
        return await this.sendToTelegram({
          url,
          type,
          fileName: resolvedFileName,
          mimeType,
          id,
          prompt,
          model
        });
      } else {
        // В браузере - скачиваем файл
        await downloadFileToBrowser(url, resolvedFileName);
        return { success: true, message: 'Файл скачан' };
      }
    } catch (error: any) {
      console.error('File delivery error:', error);
      return { 
        success: false, 
        error: error.message || 'Не удалось доставить файл',
        code: error.code
      };
    }
  }
  
  /**
   * Доставить изображение
   */
  async deliverImage(options: Omit<DeliveryOptions, 'type'>): Promise<DeliveryResult> {
    return this.deliverFile({ ...options, type: 'image' });
  }
  
  /**
   * Доставить видео
   */
  async deliverVideo(options: Omit<DeliveryOptions, 'type'>): Promise<DeliveryResult> {
    return this.deliverFile({ ...options, type: 'video' });
  }
  
  /**
   * Доставить документ
   */
  async deliverDocument(options: Omit<DeliveryOptions, 'type'>): Promise<DeliveryResult> {
    return this.deliverFile({ ...options, type: 'document' });
  }
  
  /**
   * Доставить группу файлов
   * В Mini App - отправляет альбомом
   * В браузере - скачивает по очереди
   */
  async deliverBatch(items: BatchDeliveryItem[]): Promise<DeliveryResult> {
    if (!items || items.length === 0) {
      return { success: false, error: 'Нет файлов для доставки' };
    }
    
    try {
      if (isInMiniApp()) {
        // В Mini App - отправляем альбомом
        return await this.sendBatchToTelegram(items);
      } else {
        // В браузере - скачиваем по очереди с задержкой
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          const fileName = this.generateFileName(item.type || 'image', undefined, `${i + 1}`);
          await downloadFileToBrowser(item.url, fileName);
          
          // Небольшая задержка между скачиваниями
          if (i < items.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }
        return { success: true, message: `Скачано ${items.length} файлов` };
      }
    } catch (error: any) {
      console.error('Batch delivery error:', error);
      return { 
        success: false, 
        error: error.message || 'Не удалось доставить файлы' 
      };
    }
  }
  
  // ===== TELEGRAM API =====
  
  /**
   * Отправить файл в Telegram чат
   */
  async sendToTelegram(options: DeliveryOptions): Promise<DeliveryResult> {
    try {
      const response = await this.client.post('/api/file-delivery/send', {
        url: options.url,
        type: options.type || 'auto',
        fileName: options.fileName,
        mimeType: options.mimeType,
        id: options.id,
        prompt: options.prompt,
        model: options.model
      });
      
      return response.data;
    } catch (error: any) {
      console.error('Telegram send error:', error);
      throw this.handleTelegramError(error);
    }
  }
  
  /**
   * Отправить изображение в Telegram чат
   */
  async sendImageToTelegram(
    imageUrl: string, 
    imageId: string, 
    prompt?: string, 
    model?: string, 
    isBatch?: boolean
  ): Promise<DeliveryResult> {
    try {
      const response = await this.client.post('/api/file-delivery/send-image', {
        imageUrl,
        imageId,
        prompt,
        model,
        isBatch
      });
      
      return response.data;
    } catch (error: any) {
      console.error('Error sending image to Telegram:', error);
      throw this.handleTelegramError(error);
    }
  }
  
  /**
   * Отправить видео в Telegram чат
   */
  async sendVideoToTelegram(
    videoUrl: string, 
    videoId: string, 
    prompt?: string, 
    model?: string, 
    isBatch?: boolean
  ): Promise<DeliveryResult> {
    try {
      const response = await this.client.post('/api/file-delivery/send-video', {
        videoUrl,
        videoId,
        prompt,
        model,
        isBatch
      });
      
      return response.data;
    } catch (error: any) {
      console.error('Error sending video to Telegram:', error);
      throw this.handleTelegramError(error);
    }
  }
  
  /**
   * Отправить документ в Telegram чат
   */
  async sendFileToTelegram(
    fileUrl: string, 
    fileName: string, 
    fileType: string = 'document'
  ): Promise<DeliveryResult> {
    try {
      const response = await this.client.post('/api/file-delivery/send-file', {
        fileUrl,
        fileName,
        fileType
      });
      
      return response.data;
    } catch (error: any) {
      console.error('Error sending file to Telegram:', error);
      throw this.handleTelegramError(error);
    }
  }
  
  /**
   * Отправить группу файлов в Telegram (альбом)
   */
  async sendBatchToTelegram(items: BatchDeliveryItem[]): Promise<DeliveryResult> {
    try {
      // Помечаем первый элемент
      const mediaItems = items.map((item, index) => ({
        ...item,
        isFirst: index === 0
      }));
      
      const response = await this.client.post('/api/file-delivery/send-batch', {
        mediaItems
      });
      
      return response.data;
    } catch (error: any) {
      console.error('Error sending batch to Telegram:', error);
      throw this.handleTelegramError(error);
    }
  }
  
  // ===== УТИЛИТЫ =====
  
  /**
   * Обновить URL файла если он истёк
   */
  async refreshUrl(url: string, s3Key: string): Promise<string> {
    try {
      const response = await this.client.post('/api/file-delivery/refresh-url', {
        url,
        s3Key
      });
      
      if (response.data.success) {
        return response.data.url;
      }
      
      return url;
    } catch (error) {
      console.error('Error refreshing URL:', error);
      return url;
    }
  }
  
  /**
   * Сгенерировать имя файла
   */
  private generateFileName(type: FileType, model?: string, id?: string): string {
    if (type === 'auto' || type === 'document') {
      return `file-${id || Date.now()}`;
    }
    
    return generateDownloadFileName(
      type === 'video' ? 'video' : 'image',
      model
    );
  }
  
  /**
   * Обработка ошибок Telegram
   */
  private handleTelegramError(error: any): Error {
    const responseData = error.response?.data;
    
    if (responseData?.code === 'BOT_BLOCKED') {
      const err = new Error('Бот заблокирован. Разблокируйте его в Telegram');
      (err as any).code = 'BOT_BLOCKED';
      return err;
    }
    
    if (responseData?.error) {
      return new Error(responseData.error);
    }
    
    return error;
  }
  
  // ===== LEGACY COMPATIBILITY =====
  // Методы для обратной совместимости со старым API
  
  /**
   * @deprecated Используйте sendVideoToTelegram
   */
  async sendVideoToChat(
    videoUrl: string, 
    videoId: string, 
    prompt?: string, 
    model?: string, 
    isBatch?: boolean
  ): Promise<void> {
    await this.sendVideoToTelegram(videoUrl, videoId, prompt, model, isBatch);
  }
  
  /**
   * @deprecated Используйте sendImageToTelegram
   */
  async sendImageToChat(
    imageUrl: string, 
    imageId: string, 
    prompt?: string, 
    model?: string, 
    isBatch?: boolean
  ): Promise<void> {
    await this.sendImageToTelegram(imageUrl, imageId, prompt, model, isBatch);
  }
  
  /**
   * @deprecated Используйте sendFileToTelegram
   */
  async sendFileToChat(
    fileUrl: string, 
    fileName: string, 
    fileType: string = 'document'
  ): Promise<void> {
    await this.sendFileToTelegram(fileUrl, fileName, fileType);
  }
  
  /**
   * @deprecated Используйте sendBatchToTelegram
   */
  async sendMediaGroupToChat(
    mediaItems: Array<{ url: string; type: 'image' | 'video'; isFirst?: boolean }>
  ): Promise<void> {
    await this.sendBatchToTelegram(mediaItems);
  }
}

